import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import './DocumentationV2.css';
import manifest from '../documentationV2_manifest.json';
import { documentationV2Content } from '../documentationV2Content';

const toTitleCase = (value) =>
  value
    .replace(/^\d+-/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const getRootFileLabel = (fileName) => {
  if (fileName === 'README.md') return 'Documentation Structure';
  return toTitleCase(fileName.replace('.md', ''));
};

const SidebarNode = ({ label, node, selectedFile, onSelectFile }) => {
  const [isOpen, setIsOpen] = useState(true);
  const folders = Object.keys(node).filter((key) => key !== '_files');
  const files = node._files || [];

  return (
    <li className="docv2-nav-group">
      <div
        className="docv2-group-title"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{label}</span>
        <span className="docv2-group-caret">{isOpen ? '▾' : '▸'}</span>
      </div>

      {isOpen && (
        <ul className="docv2-sub-list">
          {folders.map((folder) => (
            <SidebarNode
              key={`${label}-${folder}`}
              label={toTitleCase(folder)}
              node={node[folder]}
              selectedFile={selectedFile}
              onSelectFile={onSelectFile}
            />
          ))}

          {files.map((file) => (
            <li
              key={file.path}
              className={`docv2-file-item ${selectedFile?.path === file.path ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectFile(file);
              }}
            >
              {toTitleCase(file.name.replace('.md', ''))}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const DocChatbot = ({ isOpen, onToggle }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isInitialized) {
      initializeChatbot();
    }
  }, [isOpen, isInitialized]);

  const initializeChatbot = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const API_BASE_URL = "https://deceptive-design-server-413055756357.europe-west1.run.app";
      const response = await fetch(`${API_BASE_URL}/api/chatbot/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentationPath: 'docs',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initialize chatbot');
      }

      const data = await response.json();
      setSessionId(data.session_id);
      setIsInitialized(true);
      setMessages([
        {
          type: 'bot',
          content: "Hello! I'm your documentation assistant. Ask me anything about the docs!",
          timestamp: new Date(),
        },
      ]);
    } catch (err) {
      setError('Failed to initialize chatbot. Please try again.');
      console.error('Chatbot initialization error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const response = await fetch(`${API_BASE_URL}/api/chatbot/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          query: inputValue,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      const botMessage = {
        type: 'bot',
        content: data.answer || "I couldn't find an answer to that question.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      setError('Failed to get response. Please try again.');
      console.error('Query error:', err);

      const errorMessage = {
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`chatbot-panel ${isOpen ? 'open' : ''}`}>
      <div className="chatbot-header">
        <div className="chatbot-header-content">
          <div className="chatbot-avatar">🤖</div>
          <div className="chatbot-title">
            <h4>Doc Assistant</h4>
            <span className={`chatbot-status ${isInitialized ? 'online' : 'offline'}`}>
              {isInitialized ? 'Online' : 'Initializing...'}
            </span>
          </div>
        </div>
        <button type="button" className="chatbot-close" onClick={onToggle}>
          ✕
        </button>
      </div>

      <div className="chatbot-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.type} ${msg.isError ? 'error' : ''}`}>
            <div className="message-content">
              {msg.type === 'bot' ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: (props) => <p style={{ margin: '0.5em 0' }} {...props} />,
                    ul: (props) => <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} {...props} />,
                    ol: (props) => <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} {...props} />,
                    li: (props) => <li style={{ margin: '0.25em 0' }} {...props} />,
                    strong: (props) => <strong style={{ fontWeight: '600' }} {...props} />,
                    code: ({ inline, ...props }) =>
                      inline ? (
                        <code
                          style={{
                            background: 'rgba(0,0,0,0.1)',
                            padding: '2px 4px',
                            borderRadius: '3px',
                            fontSize: '0.9em',
                          }}
                          {...props}
                        />
                      ) : (
                        <code {...props} />
                      ),
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
              ) : (
                msg.content
              )}
            </div>
            <div className="message-time">
              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message bot typing">
            <div className="typing-indicator">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {error && <div className="chatbot-error">{error}</div>}

      <form className="chatbot-input-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          className="chatbot-input"
          placeholder="Ask about the documentation..."
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          disabled={!isInitialized || isLoading}
        />
        <button
          type="submit"
          className="chatbot-send-btn"
          disabled={!isInitialized || isLoading || !inputValue.trim()}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
        </button>
      </form>
    </div>
  );
};

const DocumentationV2 = () => {
  const defaultRootReadme = useMemo(
    () => manifest?._files?.find((f) => f.name === 'README.md') || null,
    []
  );
  const firstOverviewFile = useMemo(
    () => manifest?.['00-overview']?._files?.find((f) => f.name === 'introduction.md') || null,
    []
  );
  const [selectedFile, setSelectedFile] = useState(defaultRootReadme || firstOverviewFile);
  const [fullscreenItem, setFullscreenItem] = useState(null);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const content = selectedFile ? documentationV2Content[selectedFile.path] : '';
  const rootFiles = (manifest?._files || []).filter((file) => file.name !== 'SUMMARY.md');
  const sections = Object.keys(manifest).filter((key) => key !== '_files');
  const hasMermaidDiagram = typeof content === 'string' && content.includes('```mermaid');

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'loose',
      theme: 'default',
    });
  }, []);

  useEffect(() => {
    if (!hasMermaidDiagram) return;

    try {
      mermaid.run({
        querySelector: '.docv2-markdown .mermaid',
      });
    } catch (error) {
      console.error('Mermaid render error:', error);
    }
  }, [content, hasMermaidDiagram]);

  const MarkdownComponents = {
    code({ inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match?.[1];

      if (!inline && language === 'mermaid') {
        return (
          <div className="docv2-mermaid-wrapper">
            <div className="mermaid">{String(children).replace(/\n$/, '')}</div>
          </div>
        );
      }

      if (!inline && language) {
        return (
          <pre className={className}>
            <code {...props} className={className}>
              {children}
            </code>
          </pre>
        );
      }

      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    img({ src, alt }) {
      return (
        <button
          type="button"
          className="docv2-image-button"
          onClick={() => setFullscreenItem({ type: 'image', src, alt: alt || 'Documentation image' })}
        >
          <img src={src} alt={alt || 'Documentation image'} />
        </button>
      );
    },
  };

  return (
    <div className="docv2-layout">
      <aside className="docv2-sidebar">
        <div className="docv2-sidebar-header">Documentation Map</div>
        <div className="docv2-sidebar-content">
          <ul className="docv2-sidebar-list">
            {rootFiles.map((file) => (
              <li
                key={file.path}
                className={`docv2-file-item ${selectedFile?.path === file.path ? 'active' : ''}`}
                onClick={() => setSelectedFile(file)}
              >
                {getRootFileLabel(file.name)}
              </li>
            ))}

            {sections.map((section) => (
              <SidebarNode
                key={section}
                label={toTitleCase(section)}
                node={manifest[section]}
                selectedFile={selectedFile}
                onSelectFile={setSelectedFile}
              />
            ))}
          </ul>
        </div>
      </aside>

      <main className={`docv2-content ${chatbotOpen ? 'chatbot-open' : ''}`}>
        <article className="docv2-markdown">
          {selectedFile ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
              {content || '*No content found for this file.*'}
            </ReactMarkdown>
          ) : (
            <div className="docv2-empty-state">Select a V2 documentation page from the sidebar.</div>
          )}
        </article>
        <button
          type="button"
          className="chatbot-toggle-btn"
          title="Ask AI about documentation"
          onClick={() => setChatbotOpen((prev) => !prev)}
        >
          <span className="toggle-icon">💬</span>
          <span className="toggle-text">Ask AI</span>
        </button>
      </main>

      <DocChatbot isOpen={chatbotOpen} onToggle={() => setChatbotOpen((prev) => !prev)} />

      {fullscreenItem && (
        <div
          className="docv2-fullscreen-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setFullscreenItem(null)}
        >
          <button
            type="button"
            className="docv2-fullscreen-close"
            onClick={() => setFullscreenItem(null)}
          >
            Close
          </button>
          <div
            className="docv2-fullscreen-content"
            onClick={(event) => event.stopPropagation()}
          >
            {fullscreenItem.type === 'image' ? (
              <img src={fullscreenItem.src} alt={fullscreenItem.alt} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: fullscreenItem.svg }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentationV2;
